// MARK://dhk8/pos/stores/license-store
// The active tenant's license feature flags (SKU capabilities). The shell
// gates routes on these — see `lib/role-license-gate.ts`.
//
// Backend chain: dhk8-platform-licensing (ADR-0029). The features block is
// expected on the activate/login response and refreshable via `/me/license`;
// until that endpoint lands the store keeps a permissive default so a missing
// license row doesn't brick a till (fail-open for core POS, fail-closed for
// add-ons like KDS).
//
// NOTE: kept deliberately tiny — just the feature flags the POS shell needs.
// Billing/reports/CFDI sub-features that aren't routes don't belong here.

import { create } from 'zustand'

import { apiFetch, svcUrl } from '../lib/api.js'

/** Feature flags that gate POS routes. `true` == licensed. */
export interface PosLicense {
  /** Kitchen Display System: the `/kds` view + kitchen-role lockdown. */
  kds: boolean
  /** Operational reports (`/reports`). */
  reports: boolean
  /** Receipt/kitchen print orchestration settings (`/settings/printing`). */
  printing: boolean
  /** CFDI 4.0 stamping (drives extras on the receipt page, not a route). */
  cfdi: boolean
  /** Offline queue / LAN-relay tooling (`/offline/cola`). */
  offline: boolean
}

/** Default license: core POS on, add-ons off until the backend says otherwise. */
const DEFAULT_LICENSE: PosLicense = {
  kds: false,
  reports: true,
  printing: true,
  cfdi: false,
  offline: true,
}

interface LicenseState {
  license: PosLicense
  loaded: boolean
  /** Replace the license (e.g. from the activate/login response `license{}` block). */
  setLicense: (next: Partial<PosLicense>) => void
  /** Best-effort refresh from `/me/license`. Keeps current values on failure. */
  refresh: (signal?: AbortSignal) => Promise<void>
}

interface BackendLicenseBlock {
  features?: Partial<Record<keyof PosLicense, boolean>>
  // Some backends send a flat list of feature ids instead of a map.
  featureIds?: string[]
}

function coerce(block: BackendLicenseBlock | undefined, base: PosLicense): PosLicense {
  if (!block) return base
  if (block.features) return { ...base, ...block.features }
  if (Array.isArray(block.featureIds)) {
    const set = new Set(block.featureIds.map((s) => s.toLowerCase()))
    return {
      kds: set.has('kds'),
      reports: set.has('reports'),
      printing: set.has('printing'),
      cfdi: set.has('cfdi'),
      offline: set.has('offline'),
    }
  }
  return base
}

export const useLicenseStore = create<LicenseState>((set, get) => ({
  license: DEFAULT_LICENSE,
  loaded: false,

  setLicense(next) {
    set((s) => ({ license: { ...s.license, ...next }, loaded: true }))
  },

  async refresh(signal) {
    try {
      // GET /v1/auth/me/license (Bearer) on dhk8-platform-auth → { license: { features: {...} } }.
      const data = await apiFetch<{ license?: BackendLicenseBlock } | BackendLicenseBlock>(
        svcUrl('dhk8-platform-auth', '/v1/auth/me/license'),
        { signal },
      )
      const block: BackendLicenseBlock =
        data && typeof data === 'object' && 'license' in data && data.license
          ? (data.license as BackendLicenseBlock)
          : (data as BackendLicenseBlock)
      set({ license: coerce(block, get().license), loaded: true })
    } catch {
      // keep current (defaults) — fail-open for core POS routes
    }
  },
}))
